import { useRef, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { useModals } from 'almanac-ui/Modal'
import { Modal } from 'components/Wizards/Wizard/Modal'
import { CampaignType, ChannelType } from 'components/CampaignCreationModal/Types'
import { SEOPlanningWizardProps } from './Types'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { OrganicResult } from './SerpResponseType'
import { KeywordsResponse } from './KeywordsResponseType'
import { ChooseSites } from './ChooseSites'
import { ChoosePrimaryKeyword } from './ChoosePrimaryKeyword'
import { useMutation } from 'react-query'
import {
  CreateCampaignPayload,
  GenerateCampaignDocsPayload,
  createCampaignRequest,
  generateDocsRequest,
} from 'components/CampaignCreationModal/createRequest'
import { AiCampaignCreationError } from 'components/CampaignCreationModal/CampaignCreationModal'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AiErrorModal } from 'components/AiErrorModal/AiErrorModal'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { StepOne, Values } from './StepOne'
import { generateTitles } from '../generateTitles'
import { ChannelOption } from '../CampaignWizard/Types'
import { GeneratePrompt, ViewType } from 'types'
import { ourUuid } from 'utils'
import { DOC_PATH, FOLDER_DOC_PATH } from 'paths'
import { BlogPostLength } from '../BlogPostWizard/Types'
import { reportError } from 'utils/error'
import { Header } from 'components/Wizards/Wizard/Header/Header'
import { MultipleBlogPosts } from 'almanac-ui/icons/24'
import { PostingCadence, ScheduleContent, ScheduledContent } from '../ContentPlanning/ScheduleContent/ScheduleContent'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { GeneratedContent, SelectContent } from '../ContentPlanning/SelectContent/SelectContent'
import { Header as ContentPlanningHeader } from 'components/Wizards/ContentPlanning/Header/Header'
import { ChooseRelatedKeywords } from './ChooseRelatedKeywords'
import { SEOPlanningSchema } from 'components/Wizards/schemas/SEOPlanning'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { MobileHeader } from '../Wizard/Header/MobileHeader'
import { useCreateCardDismissal } from 'hooks/queries/useCreateCardDismissal'
import { useSEOPlanningSearch } from 'hooks/queries/SEOPlannning/useSEOPlanningSearch'
import { scheduleContent } from '../utils/postScheduling'
import { DayAndTime } from '../ContentPlanning/ScheduleContent/components/StartDateAndPostFrequency/StartDateAndPostFrequencyModal'
import { TextChannels } from '../../../constants'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { WizardProvider } from 'components/Wizards/Wizard/WizardContext'

import { Formik } from 'formik'

const SEOPlanningWizard = ({ initialValues = {} }: SEOPlanningWizardProps) => {
  const schema = SEOPlanningSchema
  const [formValues, setFormValues] = useState<Values>({
    topic: initialValues?.topic || '',
    seo_words: initialValues?.seo_words || [],
    filename: initialValues?.filename || '',
    brand_voice_id: initialValues?.brand_voice_id,
    tone: initialValues?.tone || [],
    language: initialValues?.language || '',
    project: initialValues?.project,
    goal: initialValues?.goal || '',
    audience: initialValues?.audience || '',
    blog_length: initialValues?.blog_length || BlogPostLength.Short,
    call_to_action: initialValues?.call_to_action || '',
    author_persona: initialValues?.author_persona || '',
    product_description: initialValues?.product_description || '',
    avoid: initialValues?.avoid || '',
    additional_info: initialValues?.additional_info || '',
    search_term: initialValues?.search_term || '',
    include_images: initialValues?.include_images || false,
    brand_kit_id: initialValues?.brand_kit_id,
    external_integration_id: initialValues?.external_integration_id,
  })
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedWebsites, setSelectedWebsites] = useState<OrganicResult[]>([])
  const [selectedKeyword, setSelectedKeyword] = useState<string>('')
  const [selectedRelatedKeywords, setSelectedRelatedKeywords] = useState<string[]>([])
  const [generatedTitles, setGeneratedTitles] = useState<GeneratedContent[]>([])
  const [selectedTitles, setSelectedTitles] = useState<string[]>([])
  const [scheduledTitles, setScheduledTitles] = useState<ScheduledContent[]>([])
  const [addedPrimaryKeywords, setAddedPrimaryKeywords] = useState<string[]>([])
  const [addedRelatedKeywords, setAddedRelatedKeywords] = useState<string[]>([])
  const { openModal, closeModal, closeAllModals } = useModals()
  const workspaceId = useCurrentWorkspaceId()
  const history = useHistory()
  const { currentFolder } = useCurrentFolderStore()
  const onFolderDocPath = useRouteMatch(FOLDER_DOC_PATH)
  const onDocPath = useRouteMatch(DOC_PATH)
  const onDoc = onFolderDocPath?.isExact || onDocPath?.isExact
  const { mutate: dismissCard } = useCreateCardDismissal()
  const searchResultsMutation = useSEOPlanningSearch()

  const initialStartDate = dayjs().minute(0).second(0).add(1, 'day')

  const generateTitlesMutationAbortController = useRef<AbortController | null>(null)
  if (generateTitlesMutationAbortController.current === null)
    generateTitlesMutationAbortController.current = new AbortController()
  const generateTitlesMutation = useMutation(generateTitles)

  const generateCampaignMutation = useMutation(async (payload: CreateCampaignPayload) => {
    const response = await createCampaignRequest(payload)
    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      throw new AiCampaignCreationError(
        'There was a problem generating the campaign',
        { errorCode: 'campaign' },
        {
          responseData: response.data,
          request: payload,
        },
      )
    }
    return response.data
  })

  const generateDocsMutation = useMutation(async (payload: GenerateCampaignDocsPayload) => {
    const response = await generateDocsRequest(payload)
    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      throw new AiCampaignCreationError(
        `There was a problem generating the campaign docs: ${response.data}`,
        { errorCode: 'campaign' },
        {
          responseData: response.data,
          request: {
            workspaceId,
            createCampaignResponseDataContentCampaignId: payload.campaign_id,
          },
        },
      )
    }
    return response.data
  })

  const generateKeywordsMutation = useMutation(async (sites: OrganicResult[]) => {
    const promises = sites.map((website) => {
      return sendToApi<KeywordsResponse>(`/seo-planning/keywords?site=${website.link}`)
    })

    const responses = await Promise.all(promises)

    const data: KeywordsResponse[] = responses.reduce((acc, response) => {
      if (response.type === SUCCESS_RESPONSE_TYPE) {
        acc.push(response.data)
      } else {
        reportError(new Error('Error generating keywords'), { metaData: response.data })
      }
      return acc
    }, [] as KeywordsResponse[])

    if (data.length === 0) {
      throw new Error('No keywords found')
    }

    return data
  })

  const generateRelatedKeywordsMutation = useMutation(async (keyword: string) => {
    const response = await sendToApi<KeywordsResponse>(`/seo-planning/related-keywords?keyword=${keyword}`)
    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      throw new Error()
    }
    return response.data
  })

  const generateCampaign = async () => {
    generateCampaignMutation.mutate(
      {
        workspaceId,
        name: formValues.filename,
        focus: formValues.topic,
        brand_voice_id: formValues.brand_voice_id,
        tones: formValues.tone,
        project_key: formValues.project?.id,
      },
      {
        onError: (error) => {
          openModal(AiErrorModal, {
            onCancel: closeModal,
            onTryAgain: () => {
              closeModal()
              generateCampaign()
            },
            error,
          })
        },
        onSuccess: ({ content_campaign }) => {
          dismissCard('home_generate_content_cta')
          generateDocs(content_campaign.id)
        },
      },
    )
  }

  const generateDocs = (campaignId: number) => {
    if (!scheduledTitles) return
    const channels: Array<ChannelOption> = scheduledTitles.map((title) => {
      return {
        channel_type: ChannelType.BlogPost,
        prompt_library: 'default',
        title: title.text,
        post_at: title.postAt?.toISOString(),
      }
    })

    generateDocsMutation.mutate(
      {
        workspaceId,
        campaign_id: campaignId,
        campaign_type: CampaignType.Planning,
        channels: channels,
        external_integration_id: formValues.external_integration_id,
        redirect_to_view: ViewType.Calendar,
        prompt: {
          topic: formValues.topic,
          seo_words: [selectedKeyword, ...selectedRelatedKeywords],
          brand_voice_id: formValues.brand_voice_id,
          tone: formValues.tone,
          language: formValues.language,
          goal: formValues.goal,
          audience: formValues.audience,
          blog_length: formValues.blog_length,
          call_to_action: formValues.call_to_action,
          author_persona: formValues.author_persona,
          product_description: formValues.product_description,
          avoid: formValues.avoid,
          additional_info: formValues.additional_info,
          include_images: formValues.include_images,
          brand_kit_id: formValues.brand_kit_id,
        },
      },
      {
        onError: (error) => {
          openModal(AiErrorModal, {
            onCancel: closeModal,
            onTryAgain: () => {
              closeModal()
              generateDocs(campaignId)
            },
            error,
          })
        },
        onSuccess: ({ almanac_file }) => {
          if (almanac_file.url && (almanac_file.url !== currentFolder?.url || onDoc)) history.push(almanac_file.url)
          closeAllModals()
        },
      },
    )
  }

  const generateTitleOptions = async (payload: Values, attempts: number) => {
    const prompt: GeneratePrompt = {
      ...payload,
      seo_words: [selectedKeyword, ...selectedRelatedKeywords],
      generation_type: ChannelType.BlogPost,
    }
    generateTitlesMutation.mutate(
      {
        payload: {
          prompt,
          workspaceId: workspaceId,
        },
        signal: generateTitlesMutationAbortController.current?.signal,
      },
      {
        onSuccess(data) {
          const titles = data.map((c) => ({ uuid: ourUuid(), text: c }))
          setGeneratedTitles((existingOptions) => [...existingOptions, ...titles])
          if (attempts > 0) {
            generateTitleOptions(payload, attempts - 1)
          }
        },
        onError(error) {
          reportError(error)
          openModal(AiErrorModal, {
            onCancel: () => {
              closeModal()
              goBack()
            },
            onTryAgain: () => {
              closeModal()
              setGeneratedTitles([])
              generateTitleOptions(payload, 1)
            },
          })
        },
      },
    )
  }

  const submitStepOne = async (values: Values) => {
    setFormValues(values)
    setCurrentStep(2)

    searchResultsMutation.mutate(values.search_term, {
      onError(error) {
        reportError(error)
        openModal(AiErrorModal, {
          onCancel: () => {
            closeModal()
            goBack()
          },
          onTryAgain: () => {
            closeModal()
            submitStepOne(values)
          },
        })
      },
    })
  }

  const submitStepTwo = async () => {
    setCurrentStep(3)

    generateKeywordsMutation.mutate(selectedWebsites, {
      onError(error) {
        reportError(error)
        openModal(AiErrorModal, {
          onCancel: () => {
            closeModal()
            goBack()
          },
          onTryAgain: () => {
            closeModal()
            submitStepTwo()
          },
        })
      },
    })
  }

  const submitStepThree = async () => {
    if (!selectedKeyword) return
    setCurrentStep(4)

    generateRelatedKeywordsMutation.mutate(selectedKeyword, {
      onError(error) {
        reportError(error)
        openModal(AiErrorModal, {
          onCancel: () => {
            closeModal()
            goBack()
          },
          onTryAgain: () => {
            closeModal()
            submitStepTwo()
          },
        })
      },
    })
  }

  const submitStepFour = async () => {
    setCurrentStep(5)

    generateTitleOptions(formValues, 1)
  }

  const submitStepFive = async () => {
    const titlesToSchedule = generatedTitles.filter((c) => selectedTitles.includes(c.uuid))
    let nextDay = initialStartDate

    const newScheduledTitles = titlesToSchedule.map((title) => {
      let postAt = nextDay.clone()

      nextDay = nextDay.add(1, 'days')

      return { ...title, postAt }
    })

    setScheduledTitles(newScheduledTitles)

    setCurrentStep(6)
  }

  const goBack = () => {
    if (currentStep === 2) setSelectedWebsites([])
    if (currentStep === 3) {
      setSelectedKeyword('')
      setAddedPrimaryKeywords([])
    }
    if (currentStep === 4) {
      setSelectedRelatedKeywords([])
      setAddedRelatedKeywords([])
    }
    if (currentStep === 5) {
      setSelectedTitles([])
      setGeneratedTitles([])
    }
    if (currentStep === 6) setScheduledTitles([])

    setCurrentStep((s) => s - 1)
  }

  const steps = ['Topic', 'References', 'Keywords', 'Titles', 'Plan']

  const handleScheduleChange = (schedule: { daysAndTimes: DayAndTime[]; startDate: Dayjs }) => {
    const titlesToSchedule = generatedTitles.filter((p) => selectedTitles.includes(p.uuid))
    const { daysAndTimes, startDate } = schedule
    const titles = scheduleContent(titlesToSchedule, daysAndTimes, startDate)

    setScheduledTitles(titles)
  }

  return (
    <WizardProvider schema={schema}>
      <Modal.Root>
        <ResponsiveDesign.Desktop>
          <Header
            steps={steps}
            currentStep={currentStep < 4 ? currentStep : currentStep - 1} // Account for hiding 'Related Keywords' step from header
            onStepPress={setCurrentStep}
            title={TextChannels[GenerationType.SEOPlanning].name}
            icon={MultipleBlogPosts}
            previewUrl="https://res.cloudinary.com/almanac/image/upload/v1710183787/Home%20Card%20Previews/xfpedur3adlb5odloqfm.png"
          />
        </ResponsiveDesign.Desktop>
        <ResponsiveDesign.Mobile>
          <MobileHeader
            steps={steps}
            currentStep={currentStep < 4 ? currentStep : currentStep - 1} // Account for hiding 'Related Keywords' step from header
            onStepPress={setCurrentStep}
            title={TextChannels[GenerationType.SEOPlanning].name}
            icon={MultipleBlogPosts}
          />
        </ResponsiveDesign.Mobile>
        {currentStep === 1 && <StepOne schema={schema} onSubmit={submitStepOne} values={formValues} />}

        {currentStep === 2 && (
          <ChooseSites
            searchResultsMutation={searchResultsMutation}
            selectedWebsites={selectedWebsites}
            setSelectedWebsites={setSelectedWebsites}
            onSubmit={() => submitStepTwo()}
            goBack={goBack}
          />
        )}

        {currentStep === 3 && (
          <ChoosePrimaryKeyword
            generateKeywordsMutation={generateKeywordsMutation}
            selectedKeyword={selectedKeyword}
            setSelectedKeyword={setSelectedKeyword}
            onSubmit={() => submitStepThree()}
            goBack={goBack}
            addedKeywords={addedPrimaryKeywords}
            setAddedKeywords={setAddedPrimaryKeywords}
          />
        )}

        {currentStep === 4 && (
          <ChooseRelatedKeywords
            generateRelatedKeywordsMutation={generateRelatedKeywordsMutation}
            selectedRelatedKeywords={selectedRelatedKeywords}
            primaryKeyword={selectedKeyword}
            setSelectedRelatedKeywords={setSelectedRelatedKeywords}
            onSubmit={() => submitStepFour()}
            goBack={goBack}
            addedKeywords={addedRelatedKeywords}
            setAddedKeywords={setAddedRelatedKeywords}
          />
        )}

        {currentStep === 5 && (
          <SelectContent
            header={
              <ContentPlanningHeader
                header="Select Blog Titles"
                subHeader="Select blog titles related to your topic and keywords."
              />
            }
            inputPlaceholder="Write your own title..."
            skeletonProps={{
              count: 5,
              rows: 1,
            }}
            onSubmit={submitStepFive}
            goBack={goBack}
            generateMore={() => {
              generateTitleOptions(formValues, 0)
            }}
            generatedContent={generatedTitles}
            isLoading={generateTitlesMutation.isLoading}
            setGeneratedContent={setGeneratedTitles}
            selectedContent={selectedTitles}
            setSelectedContent={setSelectedTitles}
            previousLabel="Generate Titles"
            contentType="Titles"
          />
        )}
        {currentStep === 6 && (
          <Formik initialValues={{ brand_kit_id: formValues.brand_kit_id }} onSubmit={() => {}}>
            {() => (
              <ScheduleContent
                header={
                  <ContentPlanningHeader
                    header="Plan Your Blog Calendar"
                    subHeader="Select a date and time for each of your blogs or have Blaze pick for you."
                  />
                }
                inputPlaceholder="Write your own title..."
                scheduledContent={scheduledTitles}
                setScheduledContent={setScheduledTitles}
                externalIntegrationType={ExternalIntegrationEnum.WordPress}
                onSubmit={generateCampaign}
                goBack={goBack}
                initialPostingCadence={PostingCadence.Everyday}
                onScheduleChange={handleScheduleChange}
                setCampaignName={(filename) => setFormValues({ ...formValues, filename })}
                onSelectedFolder={(project) => setFormValues({ ...formValues, project: project })}
                selectedFolder={formValues.project}
                previousLabel="Choose Titles"
                campaignName={formValues.filename}
                initialStartDate={initialStartDate}
                schema={SEOPlanningSchema}
                externalIntegrationId={formValues.external_integration_id}
              />
            )}
          </Formik>
        )}
      </Modal.Root>
    </WizardProvider>
  )
}

export { SEOPlanningWizard }
